import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const SiteChromeContext = createContext({
  headerWidgets: [],
  footerWidgets: [],
  getHeader: () => null,
  getFooter: () => null,
  loaded: false,
});

const sortByOrder = (list) =>
  [...(list || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

const findByLabel = (widgets, label) => {
  if (!widgets || !label) return null;
  const lower = String(label).toLowerCase();
  // Defensive: if two widgets share a label, prefer the most-recently-updated
  // one so admin edits always win over a stale duplicate.
  let best = null;
  for (const w of widgets) {
    if ((w.label || '').toLowerCase() !== lower) continue;
    if (!best) { best = w; continue; }
    const a = Date.parse(w.updated_at || '') || 0;
    const b = Date.parse(best.updated_at || '') || 0;
    if (a > b) best = w;
  }
  return best;
};

export function SiteChromeProvider({ children }) {
  const [headerWidgets, setHeaderWidgets] = useState([]);
  const [footerWidgets, setFooterWidgets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [h, f] = await Promise.all([
        api.get('/pages/site_header/').catch(() => ({ data: [] })),
        api.get('/pages/site_footer/').catch(() => ({ data: [] })),
      ]);
      setHeaderWidgets(sortByOrder(h.data?.results || h.data || []));
      setFooterWidgets(sortByOrder(f.data?.results || f.data || []));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Refresh chrome when admin saves header/footer (same-tab via custom event,
  // cross-tab via localStorage), and whenever the tab regains focus.
  useEffect(() => {
    const onCustom = () => fetchAll();
    const onStorage = (e) => { if (e.key === 'site-chrome-refresh') fetchAll(); };
    const onFocus = () => fetchAll();
    const onVis = () => { if (document.visibilityState === 'visible') fetchAll(); };
    window.addEventListener('site-chrome-refresh', onCustom);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('site-chrome-refresh', onCustom);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchAll]);

  const getHeader = useCallback(
    (label) => findByLabel(headerWidgets, label),
    [headerWidgets]
  );
  const getFooter = useCallback(
    (label) => findByLabel(footerWidgets, label),
    [footerWidgets]
  );

  return (
    <SiteChromeContext.Provider
      value={{ headerWidgets, footerWidgets, getHeader, getFooter, loaded, refresh: fetchAll }}
    >
      {children}
    </SiteChromeContext.Provider>
  );
}

export function useSiteChrome() {
  return useContext(SiteChromeContext);
}
